import { html, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import styles from "@/index.css?inline";
import EkoElement from "@/utilities/element";
import style from "./block-list-item.css?inline";
import "./block-list-item-light-dom.css";

declare global {
  interface Window {
    Turbo: any;
  }
}

@customElement("eko-block-list-item")
export class BlockListItem extends EkoElement {
  static styles = [unsafeCSS(styles), unsafeCSS(style)];

  private anchorRef: Ref<HTMLAnchorElement> = createRef();

  private onHostClick = (event: Event) => {
    const anchor = this.anchorRef.value;
    if (!anchor || !this.href) return;
    if (event.composedPath().includes(anchor)) return; // Avoid re-triggering if the anchor was clicked directly

    anchor.click();
  };

  @property({ reflect: true }) href?: string;
  @property({ reflect: true }) theme: "default" | "accent" | "gray" | "danger" | "success" =
    "default";
  @property({ type: String, attribute: "data-turbo" }) turbo?: string;
  @property({ type: String, attribute: "data-turbo-frame" }) turboFrame?: string;

  // Turbo has a known issue where visits don't always work when being triggered from within shadow DOM
  // https://github.com/hotwired/turbo/pull/1022
  // This is a workaround to trigger the turbo visits manually if required
  private turboClickHandler(e: Event) {
    if (this.turbo && window.Turbo) {
      const opts: Record<string, any> = {};
      if (this.turboFrame) {
        opts["frame"] = this.turboFrame;
      }

      window.Turbo.visit(this.href, opts);
      e.preventDefault();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.href) {
      this.addEventListener("click", this.onHostClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("click", this.onHostClick);
  }

  render() {
    if (this.href) {
      return html`<a
        ${ref(this.anchorRef)}
        href=${this.href}
        @click=${this.turboClickHandler}
        part="item">
        <slot></slot>
      </a>`;
    }
    return html`<div part="item"><slot></slot></div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-block-list-item": BlockListItem;
  }
}

export default BlockListItem;
